import { ApolloLink } from '@apollo/client';
import type { ASTKindToNode, Visitor } from 'graphql';
import { visit } from 'graphql';
import cloneDeep from 'lodash-es/cloneDeep';

const createShortNameGenerator = (prefix = '') => {
  let count = 0;
  return () => `${prefix}${++count}`;
};

/**
 * An ApolloLink that dynamically minifies outgoing GraphQL queries.
 *
 * @remarks
 * This link provides a client-side-only solution for reducing network payload size. It is essential
 * when using 3rd-party GraphQL APIs (like Contentful) that do not support Automatic Persisted
 * Queries (APQ).
 *
 * ### How It Works
 *
 * This link is **dynamic** and **stateless**. It processes each query individually, requiring no
 * pre-configured maps. The process is:
 *
 * 1. **Pass 1: Scan & Map** A read-only `visit` walks the AST to find all `VariableDefinition` and
 *    `FragmentDefinition` nodes.
 * 2. **Map Generation** It dynamically generates short, unique names (e.g., `v1`, `f1`) for each
 *    definition and stores them in `variableMap` and `fragmentMap`.
 * 3. **Variable Object Transform** It immediately uses `variableMap` to rename the keys in the
 *    `operation.variables` object (e.g., `{ preview: true }` becomes `{ v1: true }`).
 * 4. **Pass 2: Query AST Transform** It clones `operation.query` (to protect the cache) and runs a
 *    _second_ `visit` call. This pass uses a dedicated `visitor` object to rename all definitions
 *    (`VariableDefinition`, `FragmentDefinition`) and usages (`Variable`, `FragmentSpread`) with
 *    the new short names.
 * 5. **Forward** The fully minified `operation` (query + variables) is passed to the next link in the
 *    chain.
 *
 * @example
 *
 * ```typescript
 * import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
 * import { queryOptimizeVisitor } from './queryOptimizeVisitor'; // Name of this file
 *
 * const httpLink = createHttpLink({ uri: '...' });
 *
 * const client = new ApolloClient({
 *   cache: new InMemoryCache(),
 *   link: queryOptimizeVisitor.concat(httpLink),
 * });
 * ```
 *
 * @see {@link https://www.apollographql.com/docs/react/api/link/introduction | Apollo Link documentation}
 * @see {@link https://graphql.org/graphql-js/language/#visit | GraphQL 'visit' function}
 */
export const queryOptimizeVisitor = new ApolloLink((operation, forward) => {
  const fragmentMap: { [key: string]: string } = {};
  const variableMap: { [key: string]: string } = {};

  // Create new generators for this specific query
  const getNextFragmentName = createShortNameGenerator('f');
  const getNextVariableName = createShortNameGenerator('v');

  // Walk the AST to find all definitions and assign them a new, short name in our maps.
  visit(operation.query, {
    VariableDefinition: node => {
      const originalName = node.variable.name.value;

      if (!variableMap[originalName]) {
        variableMap[originalName] = getNextVariableName();
      }
    },
    FragmentDefinition: node => {
      const originalName = node.name.value;

      if (!fragmentMap[originalName]) {
        fragmentMap[originalName] = getNextFragmentName();
      }
    },
  });

  // transform `operation.variables`
  // { "preview": true } must become { "v1": true }
  const newVariables: { [key: string]: string } = {};

  for (const key in operation.variables) {
    const newKey = variableMap[key] || key; // Get the minified name
    newVariables[newKey] = operation.variables[key];
  }
  operation.variables = newVariables;

  // Clone the query to avoid mutating the cached original
  const newQueryAst = cloneDeep(operation.query);

  const visitor: Partial<Visitor<ASTKindToNode>> = {
    VariableDefinition: node => {
      const newName = variableMap[node.variable.name.value];

      if (!newName) {
        return false;
      }

      return {
        ...node,
        variable: { ...node.variable, name: { ...node.variable.name, value: newName } },
      };
    },
    Variable: node => {
      const newName = variableMap[node.name.value];

      if (!newName) {
        return false;
      }

      return { ...node, name: { ...node.name, value: newName } };
    },
    FragmentDefinition: node => {
      const newName = fragmentMap[node.name.value];

      if (!newName) {
        return false;
      }

      return { ...node, name: { ...node.name, value: newName } };
    },
    FragmentSpread: node => {
      const newName = fragmentMap[node.name.value];

      if (!newName) {
        return false;
      }

      return { ...node, name: { ...node.name, value: newName } };
    },
  };

  // Run the transform and replace the query on the operation
  operation.query = visit(newQueryAst, visitor);

  // Send the minified operation on its way
  return forward(operation);
});
